import React from 'react'
import styled from '@emotion/styled'

import { useCountUp } from 'react-countup'
import { Typography, Box } from '@mui/material'
import { Skeleton } from '@mui/material'
import { CircularProgressbar } from 'react-circular-progressbar'
import { GenericLoader } from '@edge/spark/ui-mui'
import { primaryColor } from '@edge/spark/ui'
import type { CardType } from '@edge/spark/io-dashboards'

import 'react-circular-progressbar/dist/styles.css'
import { renderCircleText, renderPercentage, renderSecondaryText } from './utils'
import { WidgetContainer } from '../../WidgetContainer'

const ANIMATION_DURATION_IN_SECONDS = 4

const circularProgressbarStyles = {
    path: {
        stroke: primaryColor,
        transition: 'none',
    },
    height: {
        height: '100px',
    },
    text: {
        fill: primaryColor,
        fontSize: 18,
    },
}

const CircularSkeleton = styled(Skeleton)`
    width: 80px;
    height: 80px;
`

interface Props {
    cardType: CardType
    action: {
        text: string
        link: string
    }
    count?: number | null
    total?: number | null
    loading: boolean
    text: string
    widgetId: string
    handleConfig?: (widgetId: string) => void
    readonly: boolean
}

export const CircularProgressWidget: React.FC<Props> = ({
    cardType,
    action,
    count,
    total,
    text,
    loading,
    widgetId,
    handleConfig,
    readonly,
}: Props) => {
    const secondaryText = renderSecondaryText({ count, total })
    const percentage = renderPercentage({ count, total })
    const circleText: string = renderCircleText({ count, total })
    const hasPercentage = percentage > 0

    const { countUp, update } = useCountUp({
        start: 0,
        end: 0,
        delay: 2,
        duration: ANIMATION_DURATION_IN_SECONDS,
    })

    React.useEffect(() => {
        update(percentage)
        // hasPercentage ignores NaN and 0 values
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPercentage, update])

    return (
        <WidgetContainer
            readonly={readonly}
            loading={loading}
            cardType={cardType}
            action={action}
            widgetId={widgetId}
            handleConfig={handleConfig}
        >
            <Box sx={{ m: 1, width: '80px' }}>
                <GenericLoader
                    loading={loading && !total}
                    SkeletonComponent={CircularSkeleton}
                    skeletonProps={{ variant: 'circular' }}
                >
                    <CircularProgressbar
                        styles={circularProgressbarStyles}
                        text={circleText}
                        value={typeof countUp === 'string' ? parseInt(countUp, 10) : countUp}
                        strokeWidth={12}
                    />
                </GenericLoader>
            </Box>
            <Typography variant="subtitle2">{text}</Typography>
            <Typography variant="caption" sx={{ lineHeight: 'normal' }}>
                {secondaryText}
            </Typography>
        </WidgetContainer>
    )
}

export type { Props as CircularProgressWidgetProps }
